// Per-sale accounting transfer decision for the sale drawers (Faktura /
// Betaling → components/dyrejournal/saleAccountingTransfer.js).
//
// The "Alle besøk" list (components/dyrejournal/allVisitsTable.js) renders one
// column per accounting provider and decides, per row, whether the sale is
// already transferred and which action applies (invoice / sale / credit note).
// The drawers need the very same decision so a vet can transfer an
// untransferred invoice or sale without leaving the sale. The rules below are
// lifted from the list's render*Column / bulk-send helpers — keep them in sync.
//
// Pure functions only: never mutate the sale or the customer passed in.

import { duettRowState } from './duettSaleStatus';

const PROVIDERS = ['fiken', 'tripletex', 'powerOffice', 'duett'];

const PROVIDER_NAMES = {
    fiken: 'Fiken',
    tripletex: 'Tripletex',
    powerOffice: 'PowerOffice',
    duett: 'Duett',
};

// Payment methods the list treats as a paid cash-type sale per provider
// (renderTripletexColumn / renderFikenColumn / renderPowerOfficeColumn).
const CASH_METHODS = {
    tripletex: ['cash', 'card', 'cardExternal', 'vipps', 'account', 'paypal', 'insurance', 'customerCredit'],
    fiken: ['cash', 'card', 'cardExternal', 'vipps', 'account', 'paypal', 'insurance'],
    powerOffice: ['cash', 'card', 'cardExternal', 'cardApp', 'vipps', 'account', 'bank', 'giftCard', 'paypal', 'insurance', 'customerCredit'],
};

function isPaidSale(sale) {
    return !!sale && sale.status === 1;
}

function isInvoiceSale(sale) {
    return !!sale && sale.paymentMethod === 'invoice';
}

// Tripletex/PowerOffice also treat a partially paid sale as an invoice.
function isInvoiceOrPartialSale(sale) {
    return !!sale && ['invoice', 'partial'].includes(sale.paymentMethod);
}

function isCreditNoteSale(sale) {
    return !!sale && (sale.paymentMethod === 'creditNote' || sale.status === 6);
}

// Mirrors analyzeTripletexResponses(...).invoiceCreated in allVisitsTable.js:
// an invoice exists when a queryInvoiceCreate log entry carries an invoiceNumber.
function isTripletexTransferred(sale) {
    if (!sale) return false;
    if (sale.tripletexInvoice && sale.tripletexInvoice.id) return true;
    if (sale.tripletexResponse && sale.tripletexResponse.statusCode === 201) return true;
    const responses = Array.isArray(sale.tripletexApiResponses) ? sale.tripletexApiResponses : [];
    return responses.some(r => r && r.queryInvoiceCreate && r.apiResponse && r.apiResponse.value && r.apiResponse.value.invoiceNumber);
}

// Mirrors analyzeFikenResponses(...).invoiceCreated || saleCreated.
function isFikenTransferred(sale) {
    if (!sale) return false;
    if (sale.fikenLocation && sale.fikenLocation.length > 0) return true;
    const responses = Array.isArray(sale.fikenApiResponses) ? sale.fikenApiResponses : [];
    return responses.some(r => {
        if (!r) return false;
        if (r.type && r.status) {
            return r.status === 'success' && (r.type === 'invoiceCreate' || r.type === 'saleCreate');
        }
        if (r.queryInvoiceCreate && r.apiResponse && r.apiResponse.location) return true;
        if (r.querySaleCreate && r.apiResponse && (r.apiResponse.location || r.apiResponse.saleNumber)) return true;
        return false;
    });
}

function isPowerOfficeTransferred(sale) {
    if (!sale) return false;
    if (sale.powerOfficeTransferred) return true;
    const r = sale.powerOfficeResponse;
    if (!r) return false;
    return !!(
        r.statusCode === 201
        || r.statusCode === 202
        || (r.salesOrder && (r.salesOrder.statusCode === 201 || r.salesOrder.statusCode === 202))
        || (r.cashVoucher && r.cashVoucher.statusCode === 201)
        || r.success
    );
}

// Same rules and wording as allVisitsTable.canTransferToAccounting.
function canTransferToAccounting(sale) {
    const s = sale || {};
    if (s.status === 1008) {
        return { canTransfer: false, reason: 'Krav behandlet — overføres via egenandelsfaktura og forsikringsoppgjør (egne salg)', isCreditNote: false };
    }
    if (s.status === 1007) {
        return { canTransfer: false, reason: 'Krav sendt til forsikring — kan ikke overføres før oppgjør er registrert', isCreditNote: false };
    }
    if (s.insuranceStatus === 1 || s.insuranceStatus === 2) {
        const reason = s.insuranceStatus === 2
            ? 'Sendt til forsikring — kan ikke overføres før forsikringssaken er avklart'
            : 'Venter på forsikring — kan ikke overføres før forsikringssaken er avklart';
        return { canTransfer: false, reason, isCreditNote: false };
    }
    if (isCreditNoteSale(s)) {
        return { canTransfer: true, reason: null, isCreditNote: true };
    }
    if (s.paymentMethod === 'invoiceCredited') {
        return { canTransfer: false, reason: 'Kreditert faktura kan ikke overføres', isCreditNote: false };
    }
    if (s.paymentMethod === 'saleCredited') {
        return { canTransfer: false, reason: 'Kreditert salg kan ikke overføres', isCreditNote: false };
    }
    if (s.offline >= 1) {
        return { canTransfer: false, reason: 'Slettet salg kan ikke overføres', isCreditNote: false };
    }
    return { canTransfer: true, reason: null, isCreditNote: false };
}

// Feature toggles store numeric 0/1 — coerce so a disabled provider is never
// "enabled" by a truthy string, and 0 never leaks into JSX.
function enabledAccountingProviders(customer) {
    const c = customer || {};
    const featureFlags = c.featureFlags || {};
    return PROVIDERS.filter(provider => {
        if (provider === 'fiken') {
            return !!(featureFlags.fiken || c.fikenSlug);
        }
        return !!featureFlags[provider];
    });
}

// Mirrors the "… mangler" checks at the top of each render*Column.
// The backend no longer sends fikenOAuth/fikenAccessToken/tripletexEmployeeToken/
// powerOfficeCredentials to the browser (Batch 3 — those are true secrets), so
// this must read the derived <integration>Configured booleans instead of the
// raw secret fields. fikenSlug and duettClientId are IDENTIFIERS, not secrets —
// they stay in the member customer payload, so those reads are unchanged.
function accountingProviderConfigured(provider, customer) {
    const c = customer || {};
    switch (provider) {
        case 'fiken':
            return !!c.fikenConfigured && !!c.fikenSlug;
        case 'tripletex':
            return !!c.tripletexConfigured;
        case 'powerOffice':
            return !!c.powerofficeConfigured;
        case 'duett':
            return !!c.duettClientId;
        default:
            return false;
    }
}

function base(provider, extra) {
    return { provider, providerName: PROVIDER_NAMES[provider] || provider, ...extra };
}

function tripletexAction(sale) {
    const transferCheck = canTransferToAccounting(sale);
    if (!transferCheck.canTransfer) {
        return { state: 'blocked', reason: transferCheck.reason };
    }
    if (isTripletexTransferred(sale)) {
        return { state: 'transferred' };
    }
    if (sale.tripletexVoucher && sale.tripletexVoucher.voucherId) {
        return { state: 'inVoucher', voucherNumber: sale.tripletexVoucher.voucherNumber || null };
    }
    if (transferCheck.isCreditNote) {
        if (!sale.parentSaleId) {
            return { state: 'blocked', reason: 'Mangler kobling til original faktura' };
        }
        return { state: 'ready', action: 'creditNote' };
    }
    if (isInvoiceOrPartialSale(sale)) {
        return { state: 'ready', action: 'invoice' };
    }
    if (isPaidSale(sale) && CASH_METHODS.tripletex.includes(sale.paymentMethod)) {
        return { state: 'ready', action: 'sale' };
    }
    return { state: 'notApplicable' };
}

function fikenAction(sale) {
    const transferCheck = canTransferToAccounting(sale);
    if (!transferCheck.canTransfer) {
        return { state: 'blocked', reason: transferCheck.reason };
    }
    if (isFikenTransferred(sale)) {
        return { state: 'transferred' };
    }
    if (transferCheck.isCreditNote) {
        return { state: 'notApplicable' };
    }
    if (isInvoiceSale(sale)) {
        // A paid invoice sale is booked as a Fiken sale, not an invoice (renderFikenColumn).
        return { state: 'ready', action: isPaidSale(sale) ? 'sale' : 'invoice' };
    }
    if (isPaidSale(sale) && CASH_METHODS.fiken.includes(sale.paymentMethod)) {
        return { state: 'ready', action: 'sale' };
    }
    return { state: 'notApplicable' };
}

function powerOfficeAction(sale) {
    const transferCheck = canTransferToAccounting(sale);
    if (!transferCheck.canTransfer) {
        return { state: 'blocked', reason: transferCheck.reason };
    }
    if (isPowerOfficeTransferred(sale)) {
        return { state: 'transferred' };
    }
    if (transferCheck.isCreditNote) {
        return { state: 'notApplicable' };
    }
    if (isInvoiceOrPartialSale(sale)) {
        return { state: 'ready', action: 'invoice' };
    }
    if (isPaidSale(sale) && CASH_METHODS.powerOffice.includes(sale.paymentMethod)) {
        return { state: 'ready', action: 'sale' };
    }
    return { state: 'notApplicable' };
}

function duettAction(sale) {
    const rowState = duettRowState(sale);
    const transferCheck = canTransferToAccounting(sale);
    // Same as renderDuettColumn: the ban only concerns SENDING — a sale that is
    // already in Duett keeps showing as transferred (e.g. a credited invoice).
    if (!transferCheck.canTransfer && rowState.kind !== 'transferred') {
        return { state: 'blocked', reason: transferCheck.reason };
    }
    switch (rowState.kind) {
        case 'transferred':
        case 'creditNoteSent':
            return { state: 'transferred' };
        case 'inVoucher':
            return { state: 'inVoucher', voucherNumber: rowState.voucherNumber || null };
        case 'viaVoucher':
            return { state: 'viaVoucher' };
        case 'creditNoteNoParent':
            return { state: 'blocked', reason: 'Mangler kobling til original faktura' };
        case 'creditNoteUnsent':
            return { state: 'ready', action: 'creditNote' };
        case 'unsent':
            return { state: 'ready', action: 'invoice' };
        default:
            return { state: 'notApplicable' };
    }
}

/**
 * Decide what the drawer shows for one provider.
 * @returns {{ provider, providerName, state, action?, reason?, voucherNumber? }}
 *   state: 'unconfigured' | 'blocked' | 'transferred' | 'inVoucher' | 'viaVoucher' | 'ready' | 'notApplicable'
 *   action (state 'ready' only): 'invoice' | 'sale' | 'creditNote'
 */
function accountingTransferAction(provider, sale, customer) {
    const s = sale || {};
    if (!accountingProviderConfigured(provider, customer)) {
        return base(provider, { state: 'unconfigured' });
    }
    switch (provider) {
        case 'tripletex':
            return base(provider, tripletexAction(s));
        case 'fiken':
            return base(provider, fikenAction(s));
        case 'powerOffice':
            return base(provider, powerOfficeAction(s));
        case 'duett':
            return base(provider, duettAction(s));
        default:
            return base(provider, { state: 'notApplicable' });
    }
}

// One row per enabled provider that has something to say about this sale.
function accountingTransferActions(sale, customer) {
    return enabledAccountingProviders(customer)
        .map(provider => accountingTransferAction(provider, sale, customer))
        .filter(row => row.state !== 'notApplicable');
}

export {
    PROVIDER_NAMES,
    isTripletexTransferred,
    isFikenTransferred,
    isPowerOfficeTransferred,
    canTransferToAccounting,
    enabledAccountingProviders,
    accountingProviderConfigured,
    accountingTransferAction,
    accountingTransferActions,
};
